const MB = 1024 * 1024;
const GB = 1024 * MB;

/**
 * Capture times (`takenAt`) are the camera's wall-clock time stored with a `Z` suffix. Read them back as local
 * wall-clock time (drop the zone) so a photo taken at 8:45 shows 8:45 and stays on its calendar day everywhere.
 */
export function wallClock(iso: string): Date {
  const m = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)/.exec(iso);
  return m ? new Date(m[1]!) : new Date(iso);
}

/** "1.4 GB" / "320 MB", as the prototype's selection bar. */
export function fmtSize(bytes: number): string {
  if (bytes > GB) return `${(bytes / GB).toFixed(2)} GB`;
  if (bytes >= MB) return `${Math.round(bytes / MB)} MB`;
  return bytes > 0 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : "0 KB";
}

/** "12.3 GB" for hero chips and the sidebar footer; "320 MB" while the total is under 1 GB. */
export function fmtTotal(bytes: number): string {
  return bytes >= GB ? `${(bytes / GB).toFixed(1)} GB` : `${Math.max(bytes > 0 ? 1 : 0, Math.round(bytes / MB))} MB`;
}

/** "4.2" (MB, one decimal) for single photos. */
export function fmtMB(bytes: number): string {
  return (bytes / MB).toFixed(1);
}

export function fmtCount(n: number): string {
  return n.toLocaleString('en-US');
}

/** "Jul 22 – Jul 25, 2026" / "Dec 30, 2025 – Jan 2, 2026" / "Jul 22, 2026". */
export function fmtRange(a: Date, b: Date): string {
  const o: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (a.toDateString() === b.toDateString()) return a.toLocaleDateString('en-US', { ...o, year: 'numeric' });
  return `${a.toLocaleDateString('en-US', a.getFullYear() === b.getFullYear() ? o : { ...o, year: 'numeric' })} – ${b.toLocaleDateString('en-US', { ...o, year: 'numeric' })}`;
}

export function fmtRangeIso(first: string | null | undefined, last: string | null | undefined): string {
  if (!first || !last) return '';
  const a = wallClock(first);
  const b = wallClock(last);
  return a <= b ? fmtRange(a, b) : fmtRange(b, a);
}

/** "2024–2026" or "2026". */
export function fmtYearRange(first: string, last: string): string {
  const a = wallClock(first).getFullYear();
  const b = wallClock(last).getFullYear();
  const [lo, hi] = a <= b ? [a, b] : [b, a];
  return lo === hi ? String(lo) : `${lo}–${hi}`;
}

export function fmtDayHeader(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

export function fmtLongDateTime(d: Date): string {
  return d.toLocaleString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function fmtShortDate(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "2 hours ago", "yesterday", "3 days ago"… */
export function fmtRelative(iso: string | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const s = Math.max(0, (now - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'yesterday';
  if (d < 7) return `${d} days ago`;
  const w = Math.floor(d / 7);
  if (d < 30) return `${w} week${w === 1 ? '' : 's'} ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo} month${mo === 1 ? '' : 's'} ago`;
  const y = Math.floor(d / 365);
  return `${y} year${y === 1 ? '' : 's'} ago`;
}

export function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]!.toUpperCase())
      .join('') || '?'
  );
}

/** Avatar colors from the prototype's admin tables. */
export function avatarColor(name: string): string {
  const c = ['#2b5bd0', '#b2620f', '#7244b8', '#1d7488', '#b33a5d', '#2f7a45'];
  return c[name.length % c.length]!;
}
